import {
  useEffect,
  useState,
} from 'react'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  dismiss,
  nextAnnouncement,
} from '../utils/announcements'
import type {
  Announcement,
  AnnouncementViewer,
} from '../utils/announcements'

// Renders whichever announcement applies to this visitor and hasn't been
// dismissed. Everything that varies per launch lives in the registry
// (`utils/announcements.ts`); this file is just how one looks.
//
// All three events are keyed by announcement id, so they cover every future
// entry without touching this file — and "viewed a lot, clicked never" is the
// signal that an entry has stopped earning its slot.
export function AnnouncementBanner({
  viewer,
  canConnectInbox,
  canBuyCredits,
  canUseLocationReminders,
}: {
  viewer: AnnouncementViewer | null
  /** Tri-state on purpose: `undefined` is "the flag hasn't resolved", which a
   *  predicate must be able to tell apart from "off". */
  canConnectInbox?: boolean | undefined
  /** Tri-state for the same reason. */
  canBuyCredits?: boolean | undefined
  /** Tri-state too, though its entry treats `undefined` as off — see the
   *  registry. */
  canUseLocationReminders?: boolean | undefined
}) {
  // Resolved once on mount rather than on every render: `nextAnnouncement`
  // reads localStorage, and re-reading it mid-render would make the dismissal
  // below race its own animation. Safe to freeze the viewer here because
  // ProtectedRoute holds this page back until auth has resolved, so `viewer`
  // is already the real account on the first render.
  const [announcement, setAnnouncement] = useState<Announcement | null>(() =>
    nextAnnouncement({ viewer, canConnectInbox, canBuyCredits, canUseLocationReminders }),
  )

  const shownId = announcement?.id
  useEffect(() => {
    if (shownId) {
      trackEvent(OnboardingEvents.ANNOUNCEMENT_VIEWED, { announcement: shownId })
    }
  }, [shownId])

  if (!announcement) {
    return null
  }

  const close = (event: string) => {
    trackEvent(event, { announcement: announcement.id })
    dismiss(announcement.id)
    setAnnouncement(null)
  }

  return (
    <div className="mb-6 rounded-xl border border-gray-900 bg-gray-900 p-5 text-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {/* Per-entry rather than always-on: not every notice announces a
                launch — a standing prompt badged NEW is a small untruth, and
                the badge stops meaning anything if everything wears it. */}
            {announcement.badge && (
              <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-semibold tracking-wide text-gray-900">
                {announcement.badge}
              </span>
            )}
            <h3 className="text-base font-semibold">{announcement.title}</h3>
          </div>
          <p className="mt-2 text-sm text-gray-200">{announcement.body}</p>
          {announcement.cta && (
            <a
              href={announcement.cta.href}
              // Taking the CTA is the strongest "I've read this" there is, so
              // it closes the banner too — otherwise it sits above the very
              // card the user just scrolled to.
              onClick={() => close(OnboardingEvents.ANNOUNCEMENT_CLICKED)}
              className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-900 bg-white hover:bg-gray-100 rounded-lg transition-colors"
            >
              {announcement.cta.label}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={() => close(OnboardingEvents.ANNOUNCEMENT_DISMISSED)}
          aria-label="Dismiss"
          className="shrink-0 text-sm text-gray-400 hover:text-white"
        >
          &times;
        </button>
      </div>
    </div>
  )
}
